import { REQUIREMENT_INTAKE_MODE_KEY } from '../../application/composer/mode-contract.js';

const everyTruthy = (...readers) => readers.reduce((result, read) => result && read(), true);
const firstTruthy = (...readers) => readers.reduce((result, read) => result || read(), undefined);

function resolveObject(value = null) {
  return value && typeof value === 'object' && !Array.isArray(value) ? value : null;
}

function normalizeText(value = '') {
  return String(value || '').trim();
}

function isOtherInputOptionText(text = '') {
  const normalized = normalizeText(text).toLowerCase().replace(/\s+/g, '');
  return Boolean(
    everyTruthy(() => (normalized), () => (firstTruthy(() => (['other', 'others', '其他', '其它', '输入其他', '其他,请输入', '其他，请输入', '其它,请输入', '其它，请输入'].includes(normalized)), () => (normalized.startsWith('其他(')), () => (normalized.startsWith('其他（')), () => (normalized.startsWith('其它(')), () => (normalized.startsWith('其它（')), () => (everyTruthy(() => (normalized.includes('其他')), () => (normalized.includes('输入')))), () => (everyTruthy(() => (normalized.includes('其它')), () => (normalized.includes('输入')))), () => (everyTruthy(() => (normalized.includes('其他')), () => (normalized.includes('说明')))), () => (everyTruthy(() => (normalized.includes('其它')), () => (normalized.includes('说明')))))))
  );
}

function normalizeComparableText(text = '') {
  return normalizeText(text).replace(/[\s"'“”‘’`.,，。?？!！:：;；、（）()[\]【】<>《》]/g, '');
}

function isQuestionStemOptionText(text = '', currentQuestion = null) {
  const optionText = normalizeComparableText(text);
  const questionText = normalizeComparableText(Object(currentQuestion).question_text);
  const questionLikePrefix = everyTruthy(() => (questionText.startsWith(optionText)), () => (questionText.length > optionText.length));
  return Boolean(
    everyTruthy(() => (optionText), () => (questionText), () => (optionText.length >= 8), () => (firstTruthy(() => (optionText === questionText), () => (everyTruthy(() => (questionLikePrefix), () => (/哪些|哪种|哪类|什么|是否|多少|几|如何|请问|请确认|主要用于/.test(optionText)))))))
  );
}

function buildFallbackActionPayload({ fallbackAction, currentQuestion }) {
  const fallbackPayload = firstTruthy(() => (resolveObject(Object(Object(fallbackAction).action).payload)), () => ({}));
  const fallbackTargetMode = firstTruthy(() => (normalizeText(
    Object(Object(fallbackAction).action).target_mode
    || fallbackPayload.target_mode
    || REQUIREMENT_INTAKE_MODE_KEY,
  )), () => (REQUIREMENT_INTAKE_MODE_KEY));
  const fallbackActionKey = firstTruthy(() => (normalizeText(
    Object(Object(fallbackAction).action).action_key
    || fallbackPayload.action_key
    || 'continue_collection',
  )), () => ('continue_collection'));
  const fallbackCommand = normalizeText(
    firstTruthy(() => (fallbackPayload.command), () => (fallbackPayload.action_command), () => (fallbackActionKey)),
  );
  return {
    action_key: fallbackActionKey,
    target_mode: fallbackTargetMode,
    payload: {
      ...fallbackPayload,
      action_key: fallbackActionKey,
      ...(fallbackCommand ? { command: fallbackCommand, action_command: fallbackCommand } : {}),
      target_mode: fallbackTargetMode,
      field_key: normalizeText(Object(currentQuestion).field_key),
    },
  };
}

function normalizeQuestionOption({ item, index, currentQuestion, fallbackAction }) {
  const option = firstTruthy(() => (resolveObject(item)), () => ({}));
  const text = normalizeText(firstTruthy(() => (option.label), () => (option.text), () => (option.value), () => (item)));
  if (firstTruthy(() => (!text), () => (isQuestionStemOptionText(text, currentQuestion)))) {
    return null;
  }
  const isOtherInput = isOtherInputOptionText(text);
  const requiresCustomInput = (
    firstTruthy(() => (option.requires_custom_input === true), () => (option.allow_custom_input === true), () => (isOtherInput))
  );
  return {
    key: isOtherInput ? 'other_input' : normalizeText(firstTruthy(() => (option.key), () => (text), () => (`option_${index + 1}`))),
    text,
    answerValue: isOtherInput ? '' : normalizeText(firstTruthy(() => (option.value), () => (text))),
    allowCustomInput: requiresCustomInput,
    requiresCustomInput,
    action: buildFallbackActionPayload({ fallbackAction, currentQuestion }),
    source: 'intake_question_option',
  };
}

function buildInlineOtherInputOption({ currentQuestion, fallbackAction }) {
  return {
    key: 'other_input',
    text: '输入其他',
    answerValue: '',
    allowCustomInput: true,
    requiresCustomInput: true,
    action: buildFallbackActionPayload({ fallbackAction, currentQuestion }),
    source: 'intake_question_option',
  };
}

export function buildCurrentQuestionOptions({ currentQuestion, fallbackAction }) {
  const options = Array.isArray(currentQuestion?.options) ? currentQuestion.options : [];
  const normalized = options
    .map((item, index) => normalizeQuestionOption({ item, index, currentQuestion, fallbackAction }))
    .filter(Boolean)
    .filter((option, index, source) => (
      option.key !== 'other_input'
      || source.findIndex((candidate) => candidate.key === 'other_input') === index
    ));
  const hasCustomInputOption = normalized.some((item) => item.allowCustomInput === true);
  return currentQuestion?.allow_other_input === true && !hasCustomInputOption
    ? [...normalized, buildInlineOtherInputOption({ currentQuestion, fallbackAction })]
    : normalized;
}

export function hasConcreteQuestionOption(options = []) {
  return Array.isArray(options) && options.some((item) => {
    const option = resolveObject(item) || {};
    if (normalizeText(option.key) === 'other_input') {
      return false;
    }
    if (!normalizeText(option.text) && !normalizeText(option.answerValue)) {
      return false;
    }
    return !(option.requiresCustomInput === true && !normalizeText(option.answerValue));
  });
}

export function resolveQuestionTitle(currentQuestion = null) {
  return normalizeText(currentQuestion?.question_text) || '请选择下一步操作';
}
